import * as flexsearch from "flexsearch";

import { search as searchApi } from "apis-v2/callables";
import { getAllDocuments, getEncoderOptions, fetchCompressedIndexData, search } from "common/flexsearch";
import { IGallerySlideSearchDocument } from "common/interfaces";
import { serverContext } from "js/config";
import AppController from "js/core/AppController";

import DataService from "./DataService";

interface GallerySlidesDataServiceSearchQuery {
    templateId?: string,
    fullTextSearchQuery?: string,
    size?: number,
    minScore?: number
}

interface GallerySlidesDataServiceDataItem {
    id: string,
    modifiedAt: number,
    templateId: string,
    title: string,
    description: string,
    tags: string
}

class GallerySlidesDataService extends DataService {
    protected async _prepareIndex(workspaceId: string) {
        const locale = serverContext.locale ?? "en";
        const { indexConfig, indexFileReadUrl } = await searchApi.getGallerySlidesSearchIndex({ workspaceId, locale });
        const data = await fetchCompressedIndexData(indexFileReadUrl);
        this._index = new flexsearch.Document({
            encoder: getEncoderOptions(locale),
            ...indexConfig,
        });
        data.forEach(({ key, data }) => this._index.import(key, data));
    }

    public async search(query: GallerySlidesDataServiceSearchQuery): Promise<GallerySlidesDataServiceDataItem[]> {
        await this.ensureIndexPrepared(AppController.workspaceId);

        const response: GallerySlidesDataServiceDataItem[] = [];

        let results: IGallerySlideSearchDocument[];

        if (query.fullTextSearchQuery) {
            results = search({
                index: this._index,
                query: query.fullTextSearchQuery
            });
        } else {
            results = getAllDocuments(this._index);
        }

        if (query.templateId) {
            results = results.filter(doc => doc.templateId === query.templateId);
        }

        results = results.slice(0, query.size ?? GallerySlidesDataService.SEARCH_QUERY_SIZE);

        results.forEach(({ slideId, modifiedAt, templateId, templateTitle, templateDescription, templateTags }) => {
            response.push({
                id: slideId,
                modifiedAt,
                templateId,
                title: templateTitle,
                description: templateDescription,
                tags: templateTags
            });
        });

        return response;
    }
}

export default GallerySlidesDataService;
